/**
 * AdminDashboard.js
 *
 * Location-scoped modular dashboard for Admin role
 * GOVERNANCE-FOCUSED: Approvals, User Management, Financial Controls, Audit
 * NOT operational (unlike Manager dashboard which focuses on team & complaints)
 *
 * Governance Sections:
 * 1. Governance Alerts & Actions Needed - Summary of what requires admin action
 * 2. Device Approval Workflow - Approve/reject devices entering inventory
 * 3. Financial Governance & Budget Control - Budget, costs, approvals, warranties
 * 4. User Management & Access Control - Users, roles, permissions, onboarding
 * 5. Audit & Compliance Dashboard - Audit trail, compliance checks, reports
 * 6. Security Tracking & Monitoring - Authentication and access anomalies
 *
 * Location configuration is managed via the Settings page; config gaps may surface under Governance Alerts.
 *
 */

import { useState, useEffect, useCallback, forwardRef, useImperativeHandle, useRef, Fragment } from 'react';
import { useAuth } from '../../auth/hooks/useAuth';
import BaseDashboard from './BaseDashboard';
import DashboardLayout, { Section } from './DashboardLayout';
import EmptyState from '../widgets/Shared/EmptyState';
import { DeviceIDBadge, ComplaintIDBadge, StatusBadge } from '../../../shared/components/common/Badges';
import StatsCard from '../../../shared/components/common/StatsCard';
import LoginHealthHelpModal from '../components/LoginHealthHelpModal';
import FinancialGovernanceHelpModal from '../components/FinancialGovernanceHelpModal';
import AuditComplianceSection from '../components/AuditComplianceSection';
import SecurityTrackingSection from '../components/SecurityTrackingSection';
import ComplaintView from '../../complaints/components/ComplaintView';
import { formatCurrencyDynamic, formatPercentage } from '../../../utils/numberUtils';
import BudgetUtilizationDisplay from '../../../shared/components/common/BudgetUtilizationDisplay';
import { formatRoleName } from '../../../utils/roleFormatter';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { ACTIVE_CRITICAL_COMPLAINTS_FILTER } from '../utils/complaintNavFilters';
import axios from 'axios';
import './Dashboards.css'; // Shared dashboard styles

const LOGIN_REASON_INFO = {
  wrong_password: { icon: '🔐', label: 'Wrong Password' },
  user_not_found: { icon: '👤', label: 'User Not Found' },
  account_inactive: { icon: '⛔', label: 'Account Inactive' },
};

const getLoginReasonInfo = (reason) => {
  if (LOGIN_REASON_INFO[reason]) return LOGIN_REASON_INFO[reason];
  return { icon: '❓', label: reason ? reason.replace(/_/g, ' ') : 'Unknown' };
};

const formatLoginReason = (reason) => {
  const info = getLoginReasonInfo(reason);
  return `${info.icon} ${info.label}`;
};

const formatRelativeAttempt = (lastAttempt) => {
  if (!lastAttempt) return 'N/A';
  const lastAttemptDate = new Date(lastAttempt);
  if (Number.isNaN(lastAttemptDate.getTime())) return 'N/A';
  const daysAgo = Math.floor((new Date() - lastAttemptDate) / (1000 * 60 * 60 * 24));
  if (daysAgo === 0) return 'Today';
  if (daysAgo === 1) return 'Yesterday';
  return `${daysAgo}d ago`;
};

const splitAttentionUsers = (users = []) => {
  const inactive = [];
  const pending = [];
  const seenInactive = new Set();
  const seenPending = new Set();
  users.forEach((usr) => {
    if (usr.issue_type === 'Pending Verification') {
      if (seenPending.has(usr.id)) return;
      seenPending.add(usr.id);
      pending.push(usr);
    } else if (!seenInactive.has(usr.id)) {
      seenInactive.add(usr.id);
      inactive.push(usr);
    }
  });
  return { inactive, pending };
};

/**
 * Admin Approvals Widget - Manages its own approval data refresh independently
 * This allows refreshing only the approvals section without refreshing the entire dashboard
 */
const AdminApprovalsWidget = forwardRef(({ token, onRefresh }, ref) => {
  const [approvalsData, setApprovalsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Helper function to safely format dates
  const formatApprovalDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch (e) {
      console.error('Date formatting error:', e, dateString);
      return 'N/A';
    }
  };

  const fetchApprovals = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_ENDPOINTS.APPROVALS.PENDING}?status=pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.status === 200) {
        // Transform the approvals endpoint response into the format expected by the widget
        const {
          approvals = [],
          pending_count = 0,
          approved_count = 0,
          rejected_count = 0,
          avg_approval_days = '0h',
        } = response.data;

        // Transform approvals data to ensure all required fields are present
        // Maps API response fields (from /api/approvals/pending) to component display fields
        const transformedApprovals = approvals.map(approval => {
          // Use requested_at from API (when device was submitted for approval)
          // This is the standard field name from the backend
          const requestedDate = approval.requested_at || new Date().toISOString();
          const createdDate = new Date(requestedDate);
          const today = new Date();
          const daysPending = Math.floor((today - createdDate) / (1000 * 60 * 60 * 24));

          return {
            ...approval,
            // Ensure all required fields exist with proper values
            // API returns these fields directly
            device_name: approval.device_name || `Device ${approval.device_id || 'Unknown'}`,
            device_identifier: approval.device_identifier || '',
            department: approval.department || 'Unassigned',
            department_icon: approval.department_icon || '🏢',
            // API returns manager_name (the person who requested approval), not submitted_by
            submitted_by: approval.manager_name || 'Unknown User',
            // Use manager_role from API (actual role of the user who submitted the device)
            submitted_by_role: approval.manager_role || '',
            // Cost comes from nested device_data.product_cost (the device purchase cost)
            cost: approval.device_data?.product_cost || 0,
            // Use requested_at from API for when device was submitted for approval
            created_at: requestedDate,
            days_pending: approval.days_pending || daysPending
          };
        });

        const approvalsSummary = {
          pending_count,
          approved_count,
          rejected_count,
          avg_approval_days: avg_approval_days || '0h',
          pending_approvals: transformedApprovals
        };

        setApprovalsData(approvalsSummary);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch approvals');
      console.error('Error fetching approvals:', err);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [token]);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await fetchApprovals();
    if (onRefresh) {
      onRefresh();
    }
  }, [fetchApprovals, onRefresh]);

  // Expose refresh function via ref
  useImperativeHandle(ref, () => ({
    refresh: handleRefresh,
    isRefreshing
  }), [handleRefresh, isRefreshing]);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  if (loading && !approvalsData) {
    return <div style={{ textAlign: 'center', color: '#999' }}>Loading approvals...</div>;
  }

  if (error && !approvalsData) {
    return (
      <div style={{ color: '#d32f2f', marginBottom: '1rem' }}>
        ⚠️ {error}
        <button
          onClick={() => handleRefresh()}
          style={{ marginLeft: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}
        >
          Retry
        </button>
      </div>
    );
  }

  const device_approvals = approvalsData;

  // Render the approvals content
  return (
    <>
      <style>{`
        @keyframes fadeFlicker {
          0%, 100% {
            opacity: 1;
            background-color: transparent;
          }
          50% {
            opacity: 0.5;
            background-color: rgba(59, 130, 246, 0.1);
          }
        }
      `}</style>
      <div style={{
        animation: isRefreshing ? 'fadeFlicker 0.4s ease-in-out infinite' : 'none',
        transition: 'opacity 0.2s ease, background-color 0.2s ease',
        borderRadius: '6px',
        padding: '0.5rem'
      }}>
        {device_approvals && Object.keys(device_approvals).length > 0 ? (
          <>
            {/* Help Text Box */}
            <div style={{
              backgroundColor: 'var(--dp-accent-focus-ring)',
              padding: '0.75rem',
              borderRadius: '6px',
              border: '1px solid var(--dp-accent-focus-ring)',
              marginBottom: '1rem',
              fontSize: '0.78rem',
              color: 'var(--dp-accent-secondary)',
              lineHeight: '1.5'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '0.3rem' }}>ℹ️ What this means:</div>
              <div style={{ fontSize: '0.72rem' }}><strong>Pending Approvals:</strong> Number of devices submitted for approval and currently awaiting admin review and authorization</div>
              <div style={{ marginTop: '0.2rem', fontSize: '0.72rem'}}><strong>Counts:</strong> Pending (awaiting review), Approved, Rejected, and average time to decide</div>
            </div>

          <div className="dashboard-stats-summary" style={{ marginBottom: '1.5rem' }}>
            <StatsCard
              icon="⏳"
              number={device_approvals.pending_count || 0}
              label="Pending"
              className="pending"
            />
            <StatsCard
              icon="✅"
              number={device_approvals.approved_count || 0}
              label="Approved"
              className="active"
            />
            <StatsCard
              icon="❌"
              number={device_approvals.rejected_count || 0}
              label="Rejected"
              className="open"
            />
            <StatsCard
              icon="⏱️"
              number={device_approvals.avg_approval_days || '0h'}
              label="Avg Time"
              className="total"
            />
          </div>

          {/* Pending Approvals - Smart Cards Grid */}
          {device_approvals.pending_approvals && device_approvals.pending_approvals.length > 0 && (
            <div>
              <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                📋 Pending Approvals ({device_approvals.pending_approvals.length})
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                gap: '0.75rem',
                marginBottom: '1rem'
              }}>
                {device_approvals.pending_approvals.slice(0, 6).map((approval, idx) => (
                  <div key={idx} style={{
                    padding: '0.75rem',
                    backgroundColor: '#fff',
                    borderRadius: '6px',
                    border: '1px solid #fbbf24',
                    borderLeft: '3px solid #f57c00',
                    fontSize: '0.8rem',
                    transition: 'all 0.2s',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(245, 124, 0, 0.15)';
                    e.currentTarget.style.borderLeftWidth = '4px';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderLeftWidth = '3px';
                  }}
                  >
                    {/* Device ID Badge and Name */}
                    <div style={{ fontWeight: '600', color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                        {approval.device_name || `Device ${approval.id}`}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', minHeight: '1.4rem' }}>
                      {approval.device_identifier && (
                        <DeviceIDBadge deviceId={approval.device_identifier} size="small" />
                      )}
                    </div>

                    {/* Department with Icon */}
                    {approval.department && (
                      <div style={{ fontSize: '0.7rem', color: '#666', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        <span>{approval.department_icon || '🏢'}</span>
                        <span>{approval.department}</span>
                      </div>
                    )}

                    {/* Submitted By - User Full Name with Role */}
                    {approval.submitted_by && (
                      <div style={{ fontSize: '0.7rem', color: '#333', display: 'flex', flexDirection: 'column', gap: '0.25rem', paddingTop: '0.3rem', borderTop: '1px solid #f3e5f5' }}>
                        <div style={{ fontWeight: '500' }}>
                          👤 {approval.submitted_by}
                        </div>
                        {approval.submitted_by_role && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                            <span style={{
                              backgroundColor: '#e0e7ff',
                              color: '#4f46e5',
                              padding: '0.15rem 0.35rem',
                              borderRadius: '2px',
                              fontSize: '0.65rem',
                              fontWeight: '600',
                              textTransform: 'capitalize',
                              whiteSpace: 'nowrap'
                            }}>
                              {approval.submitted_by_role.replace(/_/g, ' ')}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Cost and Days Pending */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.5rem', borderTop: '1px solid #f3e5f5' }}>
                      <div>
                        <div style={{ fontSize: '0.65rem', color: '#999', marginBottom: '0.1rem' }}>Cost</div>
                        <div style={{ fontWeight: '600', color: '#f57c00' }}>
                          {approval.cost != null ? formatCurrencyDynamic(approval.cost) : 'N/A'}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '0.65rem', color: '#999', marginBottom: '0.1rem' }}>Submitted</div>
                        <div style={{ fontWeight: '600', color: (approval.days_pending || 0) > 5 ? '#d32f2f' : (approval.days_pending || 0) > 3 ? '#f57c00' : '#4caf50', fontSize: '0.8rem' }}>
                          {formatApprovalDate(approval.created_at)}
                          <span style={{ fontSize: '0.7rem', marginLeft: '0.3rem' }}>({approval.days_pending || 0}d)</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {device_approvals.pending_approvals.length > 6 && (
                <div style={{ fontSize: '0.75rem', color: '#f57c00', textAlign: 'center', fontWeight: '500' }}>
                  +{device_approvals.pending_approvals.length - 6} more pending approvals
                </div>
              )}
            </div>
            )}
          </>
        ) : (
          <EmptyState
            section="Device Approvals"
            message="No pending device approvals. All devices have been reviewed."
            icon="✅"
          />
        )}
      </div>
    </>
  );
});

/**
 * AdminDashboard Component
 *
 * Admin-specific governance dashboard focused on:
 * - Governance alerts and required actions
 * - Device approval workflows
 * - Financial governance and budget control
 * - User and access management
 * - Audit and compliance tracking
 * - Security tracking and monitoring
 *
 * Props:
 * - onNavigateToApprovals: Callback to navigate to Device Approval page (optional)
 * - onNavigateToSettings: Callback to navigate to a Settings section (optional)
 * - onNavigateToUsers: Callback to navigate to Users with statusGroup filter (optional)
 * - onNavigateToComplaintsWithFilter: Callback to navigate to Complaints with filters (optional)
 * - token: JWT authentication token (optional, uses auth context token if not provided)
 *
 * Automatically uses authenticated user's role from auth context
 */
const AdminDashboard = ({
  onNavigateToApprovals,
  onNavigateToLocationTransfers,
  onNavigateToDevices,
  onNavigateToSettings,
  onNavigateToUsers,
  onNavigateToComplaintsWithFilter,
  token: providedToken,
  user: providedUser,
}) => {
  // Get authenticated user's role from context
  const { token: contextToken, user: contextUser } = useAuth();
  const token = providedToken || contextToken;
  const user = providedUser || contextUser;
  const [selectedDept, setSelectedDept] = useState('all');
  const [filteredRoles, setFilteredRoles] = useState(null);
  const [loadingFilter, setLoadingFilter] = useState(false);
  const [deptSearchQuery, setDeptSearchQuery] = useState('');
  const [nonCompliantSearchQuery, setNonCompliantSearchQuery] = useState('');
  const [selectedComplaintForModal, setSelectedComplaintForModal] = useState(null);
  const [complaintSortColumn, setComplaintSortColumn] = useState('created_at');
  const [complaintSortDirection, setComplaintSortDirection] = useState('asc');
  const [securityMetrics, setSecurityMetrics] = useState(null);
  const [securityMetricsLoading, setSecurityMetricsLoading] = useState(false);
  const [expandedUsernamesRow, setExpandedUsernamesRow] = useState(null);
  const [showLoginHealthHelp, setShowLoginHealthHelp] = useState(false);
  const [showFinancialGovernanceHelp, setShowFinancialGovernanceHelp] = useState(false);
  const [showDeviceAuditHelp, setShowDeviceAuditHelp] = useState(false);
  const [showSecurityHelp, setShowSecurityHelp] = useState(false);
  const [loginHealthSortColumn, setLoginHealthSortColumn] = useState('last_attempt');
  const [loginHealthSortDirection, setLoginHealthSortDirection] = useState('desc');
  const [approvalsRefreshing, setApprovalsRefreshing] = useState(false);
  const [transferCounts, setTransferCounts] = useState({ pending_outgoing: 0, pending_incoming: 0 });
  const approvalsWidgetRef = useRef(null);
  const [selectedAuditEvent, setSelectedAuditEvent] = useState(null);
  const [isAuditDrawerOpen, setIsAuditDrawerOpen] = useState(false);
  const [auditDepartmentFilter, setAuditDepartmentFilter] = useState(null);
  const [auditQuickFilter, setAuditQuickFilter] = useState('all'); // all|compliance|new|modified|deleted
  const [isCompactQuickNav, setIsCompactQuickNav] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const sync = () => setIsCompactQuickNav(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (!isAuditDrawerOpen) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsAuditDrawerOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isAuditDrawerOpen]);

  // Function to fetch security metrics - can be called on mount or on refresh
  const fetchSecurityMetrics = useCallback(async () => {
    if (!token) return;

    setSecurityMetricsLoading(true);
    try {
      // Fetch comprehensive security metrics from unified endpoint
      // Includes: failures, success_rate, and anomalies analysis
      const response = await axios.get(API_ENDPOINTS.AUTH.ADMIN_SECURITY_METRICS, {
        params: {
          days: 7,
          limit: 10,
          include: 'failures,success_rate,anomalies'
        },
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data) {
        setSecurityMetrics(response.data);
      }
    } catch (error) {
      console.error('Error fetching security metrics:', error);
      // Gracefully handle errors - don't crash dashboard
    } finally {
      setSecurityMetricsLoading(false);
    }
  }, [token]);

  // Fetch security metrics on component mount
  useEffect(() => {
    fetchSecurityMetrics();
  }, [fetchSecurityMetrics]);

  useEffect(() => {
    if (!token || user?.role !== 'admin') return;
    axios
      .get(API_ENDPOINTS.LOCATION_TRANSFERS.DASHBOARD_COUNTS, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setTransferCounts({
          pending_outgoing: res.data?.pending_outgoing ?? 0,
          pending_incoming: res.data?.pending_incoming ?? 0,
        });
      })
      .catch(() => setTransferCounts({ pending_outgoing: 0, pending_incoming: 0 }));
  }, [token, user?.role]);

  // Handle department filter change
  const handleDeptChange = async (deptCode) => {
    setSelectedDept(deptCode);
    setLoadingFilter(true);
    setFilteredRoles(null);

    try {
      const url = API_ENDPOINTS.DASHBOARD.USER_ROLES_BY_DEPT(deptCode);

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        console.error('Error fetching department filter:', response.status);
        setFilteredRoles(null);
        return;
      }

      const data = await response.json();

      if (data.success === true) {
        setFilteredRoles(data.data);
      } else {
        console.error('Department filter API error:', data);
        setFilteredRoles(null);
      }
    } catch (error) {
      console.error('Error fetching department filter:', error);
      setFilteredRoles(null);
    } finally {
      setLoadingFilter(false);
    }
  };

  // Handle complaint sorting
  const handleComplaintSort = (column) => {
    if (complaintSortColumn === column) {
      // Toggle direction if same column clicked
      setComplaintSortDirection(complaintSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column: default to asc
      setComplaintSortColumn(column);
      setComplaintSortDirection('asc');
    }
  };

  // Render sort indicator for column headers
  const renderComplaintSortIndicator = (column) => {
    if (complaintSortColumn !== column) {
      return <span style={{ marginLeft: '0.5rem', color: '#9ca3af' }}>⇅</span>;
    }
    return complaintSortDirection === 'asc'
      ? <span style={{ marginLeft: '0.5rem', color: 'var(--dp-accent-primary)' }}>↑</span>
      : <span style={{ marginLeft: '0.5rem', color: 'var(--dp-accent-primary)' }}>↓</span>;
  };

  // Sort complaints based on selected column and direction
  const getSortedComplaints = (complaints) => {
    if (!complaints || complaints.length === 0) return [];

    return [...complaints].sort((a, b) => {
      let aValue, bValue;

      if (complaintSortColumn === 'complaint_id') {
        aValue = a.complaint_identifier || '';
        bValue = b.complaint_identifier || '';
        if (complaintSortDirection === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      } else if (complaintSortColumn === 'created_at') {
        aValue = new Date(a.created_at || 0).getTime();
        bValue = new Date(b.created_at || 0).getTime();
      } else if (complaintSortColumn === 'assigned_to') {
        aValue = a.assigned_to || '';
        bValue = b.assigned_to || '';
        if (complaintSortDirection === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      } else if (complaintSortColumn === 'status') {
        aValue = a.status || '';
        bValue = b.status || '';
        if (complaintSortDirection === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      }

      // Default numeric sort for dates
      if (complaintSortDirection === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });
  };

  const handleLoginHealthSort = (column) => {
    if (loginHealthSortColumn === column) {
      setLoginHealthSortDirection(loginHealthSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setLoginHealthSortColumn(column);
      setLoginHealthSortDirection(
        ['failed_attempts', 'last_attempt', 'risk_level'].includes(column) ? 'desc' : 'asc'
      );
    }
  };

  const renderLoginHealthSortIndicator = (column) => {
    if (loginHealthSortColumn !== column) {
      return <span className="user-mgmt-login-table__sort-neutral" aria-hidden="true">⇅</span>;
    }
    return (
      <span className="user-mgmt-login-table__sort-active" aria-hidden="true">
        {loginHealthSortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  const getSortedLoginHealthRows = (rows) => {
    if (!rows || rows.length === 0) return [];

    const riskRank = { low: 1, medium: 2, high: 3 };
    const direction = loginHealthSortDirection === 'asc' ? 1 : -1;

    return [...rows].sort((a, b) => {
      let cmp = 0;

      switch (loginHealthSortColumn) {
        case 'employee_id':
          cmp = (a.employee_id || '').localeCompare(b.employee_id || '', undefined, { sensitivity: 'base' });
          break;
        case 'full_name':
          cmp = (a.full_name || '').localeCompare(b.full_name || '', undefined, { sensitivity: 'base' });
          break;
        case 'failed_attempts':
          cmp = (a.failed_attempts || 0) - (b.failed_attempts || 0);
          if (cmp === 0) {
            cmp = new Date(a.last_attempt || 0).getTime() - new Date(b.last_attempt || 0).getTime();
          }
          break;
        case 'primary_reason':
          cmp = (a.primary_reason || '').localeCompare(b.primary_reason || '', undefined, { sensitivity: 'base' });
          break;
        case 'last_attempt':
          cmp = new Date(a.last_attempt || 0).getTime() - new Date(b.last_attempt || 0).getTime();
          if (cmp === 0) {
            cmp = (a.failed_attempts || 0) - (b.failed_attempts || 0);
          }
          break;
        case 'risk_level':
          cmp = (riskRank[a.risk_level] || 0) - (riskRank[b.risk_level] || 0);
          break;
        default:
          cmp = 0;
      }

      return cmp * direction;
    });
  };

  return (
    <BaseDashboard
      token={token}
      user={user}
      onRefresh={fetchSecurityMetrics}
    >
      {(apiResponse) => {
        // API returns { data: {...}, metadata: {...} }
        // Extract the actual dashboard data from the response
        const dashboardData = apiResponse?.data || {};

        const governance_alerts = dashboardData?.governance_alerts || {};
        const critical_issues = governance_alerts?.critical_issues || {};
        const financial_governance = dashboardData?.financial_governance || {};
        const user_management = dashboardData?.user_management || {};
        const audit_events = dashboardData?.audit_events || {};
        const compliance_status = dashboardData?.compliance_status || {};
        const hasAuditComplianceData = (
          Object.keys(audit_events).length > 0 || Object.keys(compliance_status).length > 0
        );
        const hasSecurityTrackingData = securityMetricsLoading
          || (securityMetrics && Object.keys(securityMetrics).length > 0)
          || (user_management && Object.keys(user_management).length > 0);

        // Align Governance alerts 💵 card with Financial Governance payload (calendar-year Capex + pro-rated contracts)
        const fgFinance = financial_governance || {};
        const fgAnnual = Number(fgFinance?.annual_budget ?? 0);
        const fgHasBudget = fgAnnual > 0;
        const fgHighCostThreshold = Number(fgFinance?.high_cost_approval_threshold ?? 0);
        const fgHighCostThresholdLabel = fgHighCostThreshold > 0
          ? formatCurrencyDynamic(fgHighCostThreshold)
          : 'the configured threshold';
        const fgYtdCapex = Number(fgFinance?.ytd_capex ?? 0);
        const fgYtdOpex = Number(fgFinance?.ytd_opex ?? 0);
        const fgCySpend =
          fgFinance?.ytd_spend != null && fgFinance?.ytd_spend !== ''
            ? Number(fgFinance.ytd_spend)
            : fgYtdCapex + fgYtdOpex;
        const financeAlert = fgHasBudget && !!fgFinance?.budget_overrun;
        const financeUnbudgeted = !fgHasBudget;

        const complaintTat = governance_alerts?.complaint_tat || {};
        const complaintTatConfigured = !!complaintTat?.configured;

        return (
      <DashboardLayout columns="1" gap="md">

        {/* ========================================
            QUICK NAVIGATION
            ======================================== */}
        <div
          className={`admin-quick-nav${isCompactQuickNav ? ' admin-quick-nav--compact' : ''}`}
        >
          <div style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '0.75rem', color: '#6b7280' }}>
            📍 Quick Navigation
          </div>
          <div className="admin-quick-nav-scroll">
            <div
              className="admin-quick-nav-links"
              style={{
                display: 'flex',
                flexWrap: isCompactQuickNav ? 'nowrap' : 'wrap',
                gap: '0.5rem'
              }}
            >
              {[
                { label: '🚨 Alerts', id: 'governance-alerts' },
                { label: '⚠️ Critical Complaints', id: 'critical-complaints' },
                { label: '✅ Approvals', id: 'device-approvals' },
                { label: '💰 Financial', id: 'financial-governance' },
                { label: '👥 Users', id: 'user-management' },
                { label: '🔍 Audit', id: 'audit-compliance' },
                { label: '🔍 Security', id: 'security-tracking' }
              ].map((link) => (
                <a
                  key={link.id}
                  href={`#${link.id}`}
                  className="admin-quick-nav-link"
                  style={{
                    padding: '0.5rem 0.75rem',
                    backgroundColor: '#f3f4f6',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                    color: 'var(--dp-accent-primary)',
                    textDecoration: 'none',
                    fontWeight: '500',
                    transition: 'all 0.2s',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#e5e7eb';
                    e.target.style.borderColor = '#9ca3af';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#f3f4f6';
                    e.target.style.borderColor = '#e5e7eb';
                  }}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* ========================================
            SECTION 1: GOVERNANCE ALERTS & ACTIONS NEEDED
            ======================================== */}
        <Section id="governance-alerts" title="🚨 Governance Alerts & Actions Needed">
          <div className="admin-governance-alerts-grid">
            <div className="admin-governance-alerts-row admin-governance-alerts-row--4">
            {/* Device Approvals Pending */}
            <div
              className={`admin-governance-card${onNavigateToApprovals ? '' : ' admin-governance-card--no-hover'}`}
              onClick={() => onNavigateToApprovals && onNavigateToApprovals()}
              title="Open Device Approvals"
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '1.1rem' }}>📋</span>
                <div style={{ fontSize: '0.9rem', color: '#666' }}>Device Approvals Pending</div>
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: governance_alerts?.pending_approvals?.count > 0 ? '#d32f2f' : '#4caf50' }}>
                {governance_alerts?.pending_approvals?.count || 0}
              </div>
              {governance_alerts?.pending_approvals?.count > 0 && governance_alerts?.pending_approvals?.oldest_days !== null && governance_alerts?.pending_approvals?.oldest_days !== undefined && (
                <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '0.5rem' }}>
                  Oldest: {governance_alerts.pending_approvals.oldest_days} days
                </div>
              )}
              {(!governance_alerts?.pending_approvals?.count || governance_alerts?.pending_approvals?.count === 0) && (
                <div style={{ fontSize: '0.8rem', color: '#4caf50', marginTop: '0.5rem' }}>
                  ✓ All devices approved
                </div>
              )}
            </div>

            {/* Location Transfers */}
            <div
              className={`admin-governance-card${onNavigateToLocationTransfers ? '' : ' admin-governance-card--no-hover'}`}
              onClick={() => onNavigateToLocationTransfers && onNavigateToLocationTransfers()}
              title="Open Location Transfers"
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '1.1rem' }}>🚚</span>
                <div style={{ fontSize: '0.9rem', color: '#666' }}>Location Transfers</div>
              </div>
              <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#999' }}>Source approval</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: transferCounts.pending_outgoing > 0 ? '#d32f2f' : '#4caf50' }}>
                    {transferCounts.pending_outgoing}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#999' }}>Destination accept</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: transferCounts.pending_incoming > 0 ? '#d32f2f' : '#4caf50' }}>
                    {transferCounts.pending_incoming}
                  </div>
                </div>
              </div>
            </div>

            {/* Complaint TAT configuration */}
            <div
              className={[
                'admin-governance-card',
                !complaintTatConfigured && 'admin-governance-card--attention',
                !onNavigateToSettings && 'admin-governance-card--no-hover',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => {
                if (onNavigateToSettings) {
                  onNavigateToSettings('complaintTat');
                }
              }}
              title={
                onNavigateToSettings
                  ? complaintTatConfigured
                    ? 'Open Settings to review Complaint TAT'
                    : 'Open Settings to configure Complaint TAT'
                  : 'Configure Complaint TAT under Settings'
              }
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '1.1rem' }}>⏱️</span>
                <div style={{ fontSize: '0.9rem', color: '#666' }}>Complaint TAT</div>
              </div>
              <div
                style={{
                  fontSize: '1.25rem',
                  fontWeight: 'bold',
                  color: complaintTatConfigured ? '#4caf50' : '#d32f2f',
                }}
              >
                {complaintTatConfigured ? 'Configured' : 'Not configured'}
              </div>
              <div
                style={{
                  fontSize: '0.8rem',
                  color: complaintTatConfigured ? '#4caf50' : '#c62828',
                  marginTop: '0.5rem',
                }}
              >
                {complaintTatConfigured
                  ? '✓ All good — complaints can be logged'
                  : '⚠️ Set under Settings before logging complaints'}
              </div>
              {onNavigateToSettings && (
                <div style={{ fontSize: '0.75rem', color: complaintTatConfigured ? 'var(--dp-accent-primary)' : '#f57c00', marginTop: '0.5rem', fontWeight: '500' }}>
                  {complaintTatConfigured ? 'Click to review in Settings →' : 'Click to open Settings →'}
                </div>
              )}
            </div>

            {/* User Access Control Status */}
            <div
              className="admin-governance-card"
              onClick={() => scrollToSection('user-management')}
              title="View User Management & Access Control"
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '1.1rem' }}>🔐</span>
                <div style={{ fontSize: '0.9rem', color: '#666' }}>User Access Control</div>
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f57c00' }}>
                {user_management?.users_requiring_attention?.filter(u => u.issue_type !== 'Pending Verification').length || 0}
              </div>
              <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '0.5rem' }}>
                Inactive users needing re-activation (verified emails only)
              </div>
            </div>
            </div>

            <div className="admin-governance-alerts-row admin-governance-alerts-row--3">
            {/* Device Maintenance Compliance */}
            <div
              className="admin-governance-card"
              onClick={() => scrollToSection('audit-compliance')}
              title="View Device Audit & Compliance Tracking"
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '1.1rem' }}>📊</span>
                <div style={{ fontSize: '0.9rem', color: '#666' }}>Compliance Metrics</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                {/* PPM Compliance */}
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.2rem' }}>PPM</div>
                  {governance_alerts?.compliance_metrics?.ppm_compliance_rate?.value === null ? (
                    <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#9ca3af', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', width: 'fit-content', cursor: 'help', title: governance_alerts?.compliance_metrics?.ppm_compliance_rate?.reason || 'No data available' }}>
                      <span style={{ fontSize: '0.85rem' }}>ℹ️</span>
                      <span>N/A</span>
                    </div>
                  ) : (
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: (governance_alerts?.compliance_metrics?.ppm_compliance_rate || 0) >= 80 ? '#4caf50' : (governance_alerts?.compliance_metrics?.ppm_compliance_rate || 0) >= 60 ? '#ff9800' : '#d32f2f' }}>
                      {formatPercentage(governance_alerts?.compliance_metrics?.ppm_compliance_rate || 0)}
                    </div>
                  )}
                </div>

                {/* Calibration Compliance */}
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.2rem' }}>Calibration</div>
                  {governance_alerts?.compliance_metrics?.calibration_compliance_rate?.value === null ? (
                    <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#9ca3af', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', width: 'fit-content', cursor: 'help', title: governance_alerts?.compliance_metrics?.calibration_compliance_rate?.reason || 'No data available' }}>
                      <span style={{ fontSize: '0.85rem' }}>ℹ️</span>
                      <span>N/A</span>
                    </div>
                  ) : (
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: (governance_alerts?.compliance_metrics?.calibration_compliance_rate || 0) >= 80 ? '#4caf50' : (governance_alerts?.compliance_metrics?.calibration_compliance_rate || 0) >= 60 ? '#ff9800' : '#d32f2f' }}>
                      {formatPercentage(governance_alerts?.compliance_metrics?.calibration_compliance_rate || 0)}
                    </div>
                  )}
                </div>

                {/* Regulatory Compliance */}
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.2rem' }}>Regulatory</div>
                  {governance_alerts?.compliance_metrics?.regulatory_compliance_rate?.value === null ? (
                    <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#9ca3af', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', width: 'fit-content', cursor: 'help', title: governance_alerts?.compliance_metrics?.regulatory_compliance_rate?.reason || 'No data available' }}>
                      <span style={{ fontSize: '0.85rem' }}>ℹ️</span>
                      <span>N/A</span>
                    </div>
                  ) : (
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: (governance_alerts?.compliance_metrics?.regulatory_compliance_rate || 0) >= 80 ? '#4caf50' : (governance_alerts?.compliance_metrics?.regulatory_compliance_rate || 0) >= 60 ? '#ff9800' : '#d32f2f' }}>
                      {formatPercentage(governance_alerts?.compliance_metrics?.regulatory_compliance_rate || 0)}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb' }}>
                Color: Green ≥80%, Amber 60-79%, Red &lt;60%
              </div>
              {(governance_alerts?.compliance_metrics?.ppm_compliance_rate?.value === null ||
                governance_alerts?.compliance_metrics?.calibration_compliance_rate?.value === null ||
                governance_alerts?.compliance_metrics?.regulatory_compliance_rate?.value === null) && (
                <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <span style={{ fontSize: '0.8rem', cursor: 'help' }} title="No devices in the system to derive this metric">ℹ️</span>
                  <span>No devices in the system to derive this metric</span>
                </div>
              )}
            </div>

            {/* Financial Governance — same figures as 💰 Financial Governance & Budget Control (calendar-year Capex + pro-rated contracts) */}
            <div
              className="admin-governance-card"
              onClick={() => scrollToSection('financial-governance')}
              title="View Financial Governance & Budget Control"
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '1.1rem' }}>💵</span>
                <div style={{ fontSize: '0.9rem', color: '#666' }}>Financial Governance</div>
              </div>
              <BudgetUtilizationDisplay
                large
                allocatedBudget={fgFinance?.annual_budget}
                budgetUtilizationPercent={fgHasBudget ? fgFinance?.budget_utilization_percent : undefined}
                budgetOverrun={!!fgFinance?.budget_overrun}
              />
              {!fgFinance || Object.keys(fgFinance).length === 0 ? (
                <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.5rem' }}>
                  Unable to load financial governance. Try refreshing or open the Financial section below.
                </div>
              ) : financeAlert ? (
                <div style={{ fontSize: '0.8rem', color: '#c62828', marginTop: '0.5rem' }}>
                  ⚠️ {fgFinance.budget_status}
                </div>
              ) : fgHasBudget ? (
                <div style={{ fontSize: '0.8rem', color: '#4caf50', marginTop: '0.5rem' }}>
                  ✓ {fgFinance.budget_status}
                </div>
              ) : (
                <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.5rem' }}>
                  {fgFinance.budget_status}
                </div>
              )}
              {(fgAnnual > 0 || fgCySpend > 0 || (fgFinance?.pending_cost ?? 0) > 0) && Object.keys(fgFinance).length > 0 && (
                <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb' }}>
                  <div style={{ marginBottom: '0.45rem', fontWeight: '600', color: '#64748b' }}>
                    Current calendar year (Jan–Dec), approved devices
                  </div>
                  {fgHasBudget ? (
                    <div style={{ marginBottom: '0.4rem' }}>
                      Annual budget: {formatCurrencyDynamic(fgFinance.annual_budget)}
                    </div>
                  ) : (
                    <div style={{ marginBottom: '0.4rem', color: '#94a3b8' }}>
                      Annual budget not set
                    </div>
                  )}
                  <div style={{ marginBottom: '0.25rem' }}>
                    Calendar-year spend (approved): {formatCurrencyDynamic(fgCySpend)}
                  </div>
                  <div style={{ marginBottom: '0.4rem', fontSize: '0.68rem', color: '#94a3b8', paddingLeft: '0.15rem' }}>
                    YTD Capex {formatCurrencyDynamic(fgYtdCapex)}
                    {' · '}
                    YTD Opex (pro-rated contracts) {formatCurrencyDynamic(fgYtdOpex)}
                  </div>
                  {(fgFinance?.pending_cost ?? 0) > 0 && (
                    <div style={{ paddingTop: '0.4rem', borderTop: '1px solid #e5e7eb', marginTop: '0.4rem' }}>
                      <div style={{ color: '#f57c00', fontWeight: '500', marginBottom: '0.25rem' }}>
                        Pending approvals (CAPEX + OPEX): {formatCurrencyDynamic(fgFinance.pending_cost)}
                      </div>
                      {fgHasBudget ? (
                        <div style={{ color: '#f57c00', fontSize: '0.7rem' }}>
                          (+{formatPercentage(((fgFinance.pending_cost / fgAnnual) * 100))} of annual budget if approved)
                        </div>
                      ) : (
                        <div style={{ color: '#94a3b8', fontSize: '0.7rem' }}>
                          Set an annual budget to see impact on utilization if approved.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Suspicious Audit Activity */}
            <div
              className="admin-governance-card"
              onClick={() => scrollToSection('audit-compliance')}
              title="View Device Audit & Compliance Tracking"
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '1.1rem' }}>🔍</span>
                <div style={{ fontSize: '0.9rem', color: '#666' }}>Device Audit Activity</div>
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: governance_alerts?.compliance_alerts?.count > 0 ? '#d32f2f' : '#4caf50' }}>
                {governance_alerts?.compliance_alerts?.count || 0}
              </div>
              {governance_alerts?.compliance_alerts?.count > 0 && (
                <div style={{ fontSize: '0.8rem', color: '#d32f2f', marginTop: '0.5rem' }}>
                  ⚠️ {governance_alerts?.compliance_alerts?.summary || 'Suspicious activity detected'}
                </div>
              )}
              {(!governance_alerts?.compliance_alerts?.count || governance_alerts?.compliance_alerts?.count === 0) && (
                <div style={{ fontSize: '0.8rem', color: '#4caf50', marginTop: '0.5rem' }}>
                  ✓ Audit activity normal
                </div>
              )}
              {/* Help text explaining audit calculation */}
              <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb', fontStyle: 'italic' }}>
                Flags active devices with &gt;5 changes in 24h (suspicious pattern detection)
              </div>
            </div>
            </div>
          </div>
        </Section>

        {/* ========================================
            SECTION 2: TOP CRITICAL COMPLAINTS
            ======================================== */}
        <Section id="critical-complaints"
          title={(
            <span
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                gap: '1rem',
              }}
            >
              <span>
                {`🚨 Top Critical Complaints${critical_issues.critical_issues && critical_issues.critical_issues.length > 0
                  ? ` (${critical_issues.critical_issues.length})` : ''}`}
              </span>
              {onNavigateToComplaintsWithFilter ? (
                <button
                  type="button"
                  className="btn-view-all-devices"
                  onClick={() => onNavigateToComplaintsWithFilter(ACTIVE_CRITICAL_COMPLAINTS_FILTER)}
                  title="View all active critical complaints in Complaints"
                  style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                >
                  View all critical in Complaints ›
                </button>
              ) : null}
            </span>
          )}
        >
          {critical_issues.critical_issues && critical_issues.critical_issues.length > 0 ? (
            <>
              {/* Help Text */}
              <div style={{
                backgroundColor: 'var(--dp-accent-focus-ring)',
                padding: '0.75rem',
                borderRadius: '6px',
                border: '1px solid var(--dp-accent-focus-ring)',
                marginBottom: '1rem',
                fontSize: '0.75rem',
                color: 'var(--dp-accent-secondary)',
                lineHeight: '1.5'
              }}>
                <div style={{ fontWeight: '600', marginBottom: '0.3rem' }}>ℹ️ Selection Criteria:</div>
                <div>Showing top 10 critical complaints prioritized by urgency:</div>
                <div style={{ marginTop: '0.4rem', marginLeft: '0.5rem' }}>
                  <div>1. <strong>Unassigned (Open)</strong> - highest priority</div>
                  <div>2. <strong>In Progress</strong> - being worked</div>
                  <div>3. <strong>Oldest First</strong> - SLA risk indicators</div>
                </div>
              </div>
              <div className="critical-complaints-table-container">
              <table className="critical-complaints-table">
                <thead>
                  <tr>
                    <th className="col-rank">#</th>
                    <th
                      className="col-complaint"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleComplaintSort('complaint_id')}
                    >
                      Complaint {renderComplaintSortIndicator('complaint_id')}
                    </th>
                    <th className="col-device">Device</th>
                    <th className="col-department">Department</th>
                    <th
                      className="col-reported"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleComplaintSort('created_at')}
                    >
                      Reported {renderComplaintSortIndicator('created_at')}
                    </th>
                    <th
                      className="col-assigned"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleComplaintSort('assigned_to')}
                    >
                      Assigned To {renderComplaintSortIndicator('assigned_to')}
                    </th>
                    <th
                      className="col-status"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleComplaintSort('status')}
                    >
                      Status {renderComplaintSortIndicator('status')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedComplaints(critical_issues.critical_issues.slice(0, 10)).map((complaint, index) => {
                    const getDaysSince = (dateString) => {
                      if (!dateString) return 'N/A';
                      const date = new Date(dateString);
                      const today = new Date();
                      const days = Math.floor((today - date) / (1000 * 60 * 60 * 24));
                      if (days === 0) return 'Today';
                      if (days === 1) return '1 day ago';
                      return `${days} days ago`;
                    };

                    return (
                      <tr
                        key={index}
                        className="complaint-row clickable"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedComplaintForModal(complaint)}
                      >
                        <td className="col-rank">
                          <span className="rank-badge">{index + 1}</span>
                        </td>
                        <td className="col-complaint complaint-stacked-cell">
                          <div className="stacked-content">
                            <span className="complaint-title">{complaint.title}</span>
                            <ComplaintIDBadge complaintId={complaint.complaint_identifier || 'N/A'} size="small" />
                          </div>
                        </td>
                        <td className="col-device device-stacked-cell">
                          <div className="stacked-content">
                            <span className="device-name">{complaint.device_name}</span>
                            <DeviceIDBadge deviceId={complaint.device_identifier || 'N/A'} size="small" />
                          </div>
                        </td>
                        <td className="col-department">
                          <span className="department-icon-small">{complaint.department_icon || '🏢'}</span>
                          <span className="department-text">{complaint.department || 'N/A'}</span>
                        </td>
                        <td className="col-reported">
                          <span className="reported-text">{getDaysSince(complaint.created_at)}</span>
                        </td>
                        <td className="col-assigned">
                          <span className="assigned-text">{complaint.assigned_to || '—'}</span>
                        </td>
                        <td className="col-status center">
                          <StatusBadge status={complaint.status || 'unknown'} size="small" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          ) : (
            <EmptyState section="Critical Complaints" message="No critical complaints at this time." icon="🚨" />
          )}
        </Section>

        {/* ========================================
            SECTION 3: DEVICE APPROVAL WORKFLOW
            ======================================== */}
        <Section
          id="device-approvals"
          title={
            <span
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                gap: '1rem'
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                ✅ Device Approval Workflow
                <button
                  type="button"
                  className="btn-refresh"
                  onClick={() => {
                    setApprovalsRefreshing(true);
                    approvalsWidgetRef.current?.refresh().finally(() => {
                      setApprovalsRefreshing(false);
                    });
                  }}
                  disabled={approvalsRefreshing}
                  title="Refresh device approvals"
                  style={{
                    padding: '0.25rem',
                    backgroundColor: 'transparent',
                    border: 'none',
                    minWidth: 'auto',
                    minHeight: 'auto',
                    cursor: approvalsRefreshing ? 'not-allowed' : 'pointer',
                    opacity: approvalsRefreshing ? 0.6 : 1,
                    animation: approvalsRefreshing ? 'spin 1s linear infinite' : 'none'
                  }}
                >
                  🔄
                </button>
              </span>
              {onNavigateToApprovals ? (
                <button
                  type="button"
                  className="btn-view-all-devices"
                  onClick={onNavigateToApprovals}
                  title="Open the full device approval workflow"
                  style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                >
                  View Full Approval Workflow ›
                </button>
              ) : null}
            </span>
          }
        >
          <AdminApprovalsWidget ref={approvalsWidgetRef} token={token} />
        </Section>

        {/* ========================================
            SECTION 4: FINANCIAL GOVERNANCE & BUDGET CONTROL
            ======================================== */}
        <Section
          id="financial-governance"
          title={(
            <span
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                gap: '1rem',
              }}
            >
              <span>💰 Financial Governance & Budget Control</span>
              <button
                type="button"
                className="user-mgmt-help-btn"
                onClick={() => setShowFinancialGovernanceHelp(true)}
                aria-label="Financial governance help"
                title="What do these metrics mean?"
                style={{ flexShrink: 0 }}
              >
                ?
              </button>
            </span>
          )}
        >
          {financial_governance && Object.keys(financial_governance).length > 0 ? (
            <>
              <div className="list-default-window-note" role="note">
                <span className="list-default-window-note__icon" aria-hidden="true">ℹ️</span>
                <span>
                  Location-scoped calendar-year YTD. <strong>Capex</strong> = approved devices;{' '}
                  <strong>Opex</strong> = pro-rated contracts. Pending = budget impact if approved.
                </span>
              </div>

              <div className={`fg-budget-card ${financeAlert ? 'fg-budget-card--alert' : financeUnbudgeted ? 'fg-budget-card--unbudgeted' : 'fg-budget-card--ok'}`}>
                <div className={`fg-budget-card__header ${financeAlert ? 'fg-budget-card__header--alert' : financeUnbudgeted ? 'fg-budget-card__header--unbudgeted' : 'fg-budget-card__header--ok'}`}>
                  {financeAlert ? '⚠️ Budget Alert' : financeUnbudgeted ? '📌 Calendar-year spend (annual budget not set)' : '✅ Budget On Track'}
                </div>
                {(() => {
                  const annualB = financial_governance.annual_budget ?? 0;
                  const hasBudget = annualB > 0;
                  const pctOfBudget = (amount) => (
                    hasBudget ? formatPercentage((Number(amount) / annualB) * 100) : null
                  );
                  const utilizationPct = fgHasBudget
                    ? financial_governance.budget_utilization_percent
                    : null;

                  return (
                    <div className="fg-budget-card__metrics">
                      <div className="fg-budget-card__metric-cell">
                        <div className="fg-budget-card__metric-label">Annual Budget</div>
                        <div className="fg-budget-card__metric-value">
                          {formatCurrencyDynamic(financial_governance.annual_budget)}
                        </div>
                        <div className="fg-budget-card__metric-subtext">Approved budget</div>
                      </div>
                      <div className="fg-budget-card__metric-separator" aria-hidden="true" />
                      <div className="fg-budget-card__metric-cell">
                        <div className="fg-budget-card__metric-label">YTD Capex</div>
                        <div className={`fg-budget-card__metric-value fg-budget-card__metric-value--capex${financeAlert ? ' fg-budget-card__metric-value--capex-alert' : ''}`}>
                          {formatCurrencyDynamic(financial_governance.ytd_capex)}
                          {financeAlert && (
                            <span style={{ fontSize: '0.8rem', color: '#d32f2f' }}>⚠️</span>
                          )}
                        </div>
                        <div className="fg-budget-card__metric-subtext">
                          {hasBudget
                            ? <>{pctOfBudget(financial_governance.ytd_capex)} of budget</>
                            : <span className="fg-budget-card__metric-subtext--muted">Calendar-year approved devices</span>}
                        </div>
                      </div>
                      <div className="fg-budget-card__metric-separator" aria-hidden="true" />
                      <div className="fg-budget-card__metric-cell">
                        <div className="fg-budget-card__metric-label">YTD Opex</div>
                        <div className="fg-budget-card__metric-value fg-budget-card__metric-value--opex">
                          {formatCurrencyDynamic(financial_governance.ytd_opex)}
                        </div>
                        <div className="fg-budget-card__metric-subtext">
                          {hasBudget
                            ? <>{pctOfBudget(financial_governance.ytd_opex)} of budget</>
                            : <span className="fg-budget-card__metric-subtext--muted">Pro-rated contracts</span>}
                        </div>
                      </div>
                      <div className="fg-budget-card__metric-separator" aria-hidden="true" />
                      <div className="fg-budget-card__metric-cell">
                        <div className="fg-budget-card__metric-label">Pending Approvals</div>
                        <div className="fg-budget-card__metric-value fg-budget-card__metric-value--pending">
                          {formatCurrencyDynamic(financial_governance.pending_cost)}
                        </div>
                        <div className="fg-budget-card__metric-subtext">
                          {hasBudget
                            ? <>{pctOfBudget(financial_governance.pending_cost)} of budget</>
                            : <span className="fg-budget-card__metric-subtext--muted">If approved</span>}
                        </div>
                      </div>
                      <div className="fg-budget-card__metric-separator" aria-hidden="true" />
                      <div className="fg-budget-card__metric-cell">
                        <div className="fg-budget-card__metric-label">Remaining</div>
                        <div className={`fg-budget-card__metric-value ${financial_governance.budget_remaining > 0 ? 'fg-budget-card__metric-value--remaining-ok' : 'fg-budget-card__metric-value--remaining-bad'}`}>
                          {formatCurrencyDynamic(financial_governance.budget_remaining)}
                        </div>
                        <div className="fg-budget-card__metric-subtext">
                          {hasBudget
                            ? <>{pctOfBudget(financial_governance.budget_remaining)} of budget</>
                            : <span className="fg-budget-card__metric-subtext--muted">After YTD spend</span>}
                        </div>
                      </div>
                      <div className="fg-budget-card__metric-separator" aria-hidden="true" />
                      <div className="fg-budget-card__metric-cell">
                        <div className="fg-budget-card__metric-label">Utilization</div>
                        {fgHasBudget && utilizationPct != null ? (
                          <>
                            <div className={`fg-budget-card__metric-value fg-budget-card__metric-value--utilization${financial_governance.budget_overrun ? ' fg-budget-card__metric-value--utilization-over' : ''}`}>
                              {formatPercentage(utilizationPct)}
                            </div>
                            <div className="fg-budget-card__metric-subtext">YTD total utilization</div>
                          </>
                        ) : (
                          <>
                            <div className="fg-budget-card__metric-value" style={{ fontSize: '1rem' }}>
                              Unbudgeted
                            </div>
                            <div className="fg-budget-card__metric-subtext fg-budget-card__metric-subtext--muted">
                              Set location budget to track
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {financial_governance.pending_cost > 0 && (
                  (() => {
                    const currentSpend = financial_governance.ytd_capex + financial_governance.ytd_opex;
                    const projectedSpend = currentSpend + financial_governance.pending_cost;
                    const annualB = financial_governance.annual_budget ?? 0;
                    const hasBudget = annualB > 0;
                    const isAlreadyOverBudget = hasBudget && currentSpend > annualB;
                    const willExceedBudget = hasBudget && projectedSpend > annualB;

                    return (
                      <div className={`fg-budget-card__impact${isAlreadyOverBudget ? ' fg-budget-card__impact--over' : ''}`}>
                        <div className="fg-budget-card__impact-head">
                          📋 <strong>Budget Impact</strong> - If all pending approvals are approved:
                        </div>
                        <div className="fg-budget-card__impact-row">
                          <span>
                            Total spend: {formatCurrencyDynamic(projectedSpend)}
                            {hasBudget
                              ? <> ({formatPercentage((projectedSpend / annualB) * 100)} utilization)</>
                              : <> (no annual budget allocated — utilization not calculated)</>}
                          </span>
                          <span style={{ fontWeight: '600', color: isAlreadyOverBudget ? '#d32f2f' : willExceedBudget ? '#d32f2f' : '#2e7d32' }}>
                            {isAlreadyOverBudget && willExceedBudget && '🔴 Already exceeded + further exceed'}
                            {isAlreadyOverBudget && !willExceedBudget && '⚠️ Already over budget'}
                            {!isAlreadyOverBudget && willExceedBudget && '⚠️ Would exceed'}
                            {!isAlreadyOverBudget && !willExceedBudget && '✅ Within budget'}
                          </span>
                        </div>
                        {isAlreadyOverBudget && (
                          <div className="fg-budget-card__impact-detail">
                            ⚠️ Current approved spend ({formatCurrencyDynamic(currentSpend)}) has already exceeded the annual budget ({formatCurrencyDynamic(annualB)}) by {formatCurrencyDynamic(currentSpend - annualB)}.
                            {willExceedBudget && (
                              <div style={{ marginTop: '0.25rem' }}>
                                Approving pending items will further increase overrun by {formatCurrencyDynamic(financial_governance.pending_cost)}.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()
                )}

                <div className="fg-budget-card__status">
                  {financial_governance.budget_status}
                </div>
              </div>

              {/* New Financial Governance Stats Cards */}
              <div className="financial-cards-grid responsive-grid responsive-grid-200" style={{ marginBottom: '1.5rem' }}>
                {/* Total Capex Card */}
                <div style={{
                  backgroundColor: '#fff',
                  padding: '1rem',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  borderLeft: '4px solid #1976d2'
                }}>
                  <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.5rem', fontWeight: '600' }}>
                    💰 Total Capex
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1976d2', marginBottom: '0.5rem' }}>
                    {formatCurrencyDynamic(financial_governance.total_capex)}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#4b5563', fontWeight: '500', lineHeight: '1.4' }}>
                    Total device investment
                  </div>
                </div>

                {/* Total Maintenance Contracts Cost Card */}
                <div style={{
                  backgroundColor: '#fff',
                  padding: '1rem',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  borderLeft: '4px solid #4caf50'
                }}>
                  <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.5rem', fontWeight: '600' }}>
                    📋 Total Maintenance Contracts
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#4caf50', marginBottom: '0.5rem' }}>
                    {formatCurrencyDynamic(financial_governance.total_maintenance_contracts_cost)}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#4b5563', fontWeight: '500', lineHeight: '1.4' }}>
                    LAMC + CAMC cost
                  </div>
                </div>

                {/* Annual Maintenance Contracts Card */}
                <div style={{
                  backgroundColor: '#fff',
                  padding: '1rem',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  borderLeft: '4px solid #f57c00'
                }}>
                  <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.5rem', fontWeight: '600' }}>
                    💸 Annual Contract Cost
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#f57c00', marginBottom: '0.5rem' }}>
                    {formatCurrencyDynamic(financial_governance.annual_contract_cost)}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#4b5563', fontWeight: '500', lineHeight: '1.4' }}>
                    Yearly maintenance contract cost
                  </div>
                </div>

                {/* Contract to Value Ratio Card */}
                <div style={{
                  backgroundColor: financial_governance.contract_to_value_ratio > 15 ? '#f3e5f5' : '#fff',
                  padding: '1rem',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  borderLeft: '4px solid #9c27b0'
                }}>
                  <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.5rem', fontWeight: '600' }}>
                    📈 Contract to Value Ratio
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: financial_governance.contract_to_value_ratio > 15 ? '#c2185b' : '#9c27b0', marginBottom: '0.5rem' }}>
                    {formatPercentage(financial_governance.contract_to_value_ratio)}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#4b5563', fontWeight: '500', lineHeight: '1.4' }}>
                    Annual cost as % of total value
                  </div>
                  {financial_governance.contract_to_value_ratio > 15 && (
                    <div style={{ fontSize: '0.65rem', color: '#c2185b', marginTop: '0.5rem', fontWeight: '500' }}>
                      ⚠️ High maintenance ratio
                    </div>
                  )}
                </div>
              </div>

              <div className="dashboard-subsection-label">Approvals & coverage</div>

              <div className="fg-approvals-coverage-grid">
                <div className="fg-high-cost-strip">
                <div className="fg-high-cost-strip__header">
                  <div className="fg-high-cost-strip__title">⚠️ High-Cost Approvals</div>
                  <button
                    type="button"
                    className="fg-high-cost-strip__link"
                    onClick={() => scrollToSection('device-approvals')}
                  >
                    View in Approvals →
                  </button>
                </div>
                <div className="fg-high-cost-strip__metrics">
                  <div>
                    <div className="fg-high-cost-strip__metric-label">Pending &gt;{fgHighCostThresholdLabel}</div>
                    <div className="fg-high-cost-strip__metric-value fg-high-cost-strip__metric-value--pending">
                      {financial_governance.high_cost_pending || 0}
                    </div>
                  </div>
                  <div>
                    <div className="fg-high-cost-strip__metric-label">{financial_governance.approval_rate_label}</div>
                    <div className="fg-high-cost-strip__metric-value fg-high-cost-strip__metric-value--rate">
                      {formatPercentage(financial_governance.approval_rate || 0)}
                    </div>
                  </div>
                </div>
                {financial_governance.high_cost_pending_list && financial_governance.high_cost_pending_list.length > 0 ? (
                  <>
                    <div className="fg-high-cost-strip__devices">
                      {financial_governance.high_cost_pending_list.slice(0, 4).map((device, idx) => (
                        <div key={idx} className="fg-high-cost-strip__device">
                          <div className="fg-high-cost-strip__device-name">
                            {device.device_name || 'N/A'}
                          </div>
                          <div className="fg-high-cost-strip__device-meta">
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {device.department_icon} {device.department || 'N/A'}
                            </span>
                            <span className="fg-high-cost-strip__device-cost">{formatCurrencyDynamic(device.cost)}</span>
                          </div>
                          <div className="fg-high-cost-strip__device-days">
                            {device.days_pending}d pending
                          </div>
                        </div>
                      ))}
                    </div>
                    {financial_governance.high_cost_pending_list.length > 4 && (
                      <div className="fg-high-cost-strip__more">
                        +{financial_governance.high_cost_pending_list.length - 4} more devices
                      </div>
                    )}
                  </>
                ) : (
                  <p className="fg-high-cost-strip__empty">No high-cost devices pending approval.</p>
                )}
                </div>

                <div className="fg-coverage-card">
                  <div className="fg-coverage-card__title">🔧 Asset Coverage Snapshot</div>
                  <div className="fg-coverage-card__metrics">
                    <div>
                      <div className="fg-coverage-card__metric-label">Coverage Expiring</div>
                      <div className="fg-coverage-card__metric-value fg-coverage-card__metric-value--expiring">
                        {financial_governance.coverage_expiring || 0}
                      </div>
                    </div>
                    <div>
                      <div className="fg-coverage-card__metric-label">No Coverage (In-house)</div>
                      <div className="fg-coverage-card__metric-value fg-coverage-card__metric-value--nocov">
                        {financial_governance.no_coverage || 0}
                      </div>
                    </div>
                    <div>
                      <div className="fg-coverage-card__metric-label">Coverage Expired</div>
                      <div className="fg-coverage-card__metric-value fg-coverage-card__metric-value--expired">
                        {financial_governance.coverage_expired || 0}
                      </div>
                    </div>
                  </div>
                  <div className="fg-coverage-card__distribution">
                    <div className="fg-coverage-card__distribution-label">Service Distribution</div>
                    <div className="fg-coverage-card__distribution-stats">
                      {[
                        { icon: '🏥', label: 'Warranty', count: financial_governance.warranty_count || 0 },
                        { icon: '🔧', label: 'LAMC', count: financial_governance.lamc_count || 0 },
                        { icon: '⚙️', label: 'CAMC', count: financial_governance.camc_count || 0 },
                        { icon: '🏢', label: 'In-house', count: financial_governance.inhouse_count || 0 },
                      ].map((item, idx) => (
                        <Fragment key={item.label}>
                          {idx > 0 && <div className="fg-coverage-card__distribution-separator" aria-hidden="true" />}
                          <div className="fg-coverage-card__distribution-stat">
                            <div className="fg-coverage-card__distribution-stat-label">
                              {item.icon} {item.label}
                            </div>
                            <div className="fg-coverage-card__distribution-stat-value">{item.count}</div>
                          </div>
                        </Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <FinancialGovernanceHelpModal
                isOpen={showFinancialGovernanceHelp}
                onClose={() => setShowFinancialGovernanceHelp(false)}
                highCostThresholdLabel={fgHighCostThresholdLabel}
              />
            </>
          ) : (
            <EmptyState
              section="Financial Governance"
              message="No financial governance data available."
              icon="📊"
            />
          )}
        </Section>

        {/* ========================================
            SECTION 5: USER MANAGEMENT & ACCESS CONTROL
            ======================================== */}
        <Section id="user-management" title="👥 User Management & Access Control">
          {user_management && Object.keys(user_management).length > 0 ? (
            <>
              {(() => {
                const rosterActive = (filteredRoles?.total_active !== undefined
                  ? filteredRoles.total_active
                  : user_management.total_active) || 0;
                const rosterInactive = (filteredRoles?.inactive_users !== undefined
                  ? filteredRoles.inactive_users
                  : user_management.inactive_users) || 0;
                const rosterPending = (filteredRoles?.pending_verifications !== undefined
                  ? filteredRoles.pending_verifications
                  : user_management.pending_verifications) || 0;
                const passwordSetupPending = (filteredRoles?.password_setup_pending !== undefined
                  ? filteredRoles.password_setup_pending
                  : user_management.password_setup_pending) || 0;
                const roleList = filteredRoles?.roles || user_management.roles || [];
                const { inactive: inactivePreview, pending: pendingPreview } = splitAttentionUsers(
                  user_management.users_requiring_attention || []
                );
                const inactiveKpi = user_management.inactive_users || 0;
                const pendingKpi = user_management.pending_verifications || 0;
                const canOpenUsers = typeof onNavigateToUsers === 'function';
                const openUsers = (statusGroup) => {
                  if (canOpenUsers) onNavigateToUsers(statusGroup);
                };

                const renderAttentionCard = (usr, variant) => {
                  const isPending = variant === 'pending';
                  const refDate = isPending
                    ? (usr.created_at ? new Date(usr.created_at) : null)
                    : (usr.made_inactive_at ? new Date(usr.made_inactive_at) : null);
                  const days = refDate
                    ? Math.floor((new Date() - refDate) / (1000 * 60 * 60 * 24))
                    : 0;
                  const dateLabel = refDate
                    ? refDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
                    : 'N/A';
                  const durationClass = isPending
                    ? (days > 3 ? 'user-mgmt-attention-card__duration--warn' : '')
                    : (days > 30 ? 'user-mgmt-attention-card__duration--critical' : days > 14 ? 'user-mgmt-attention-card__duration--warn' : '');
                  const statusGroup = isPending ? 'pending' : 'deactivated';
                  const tooltip = isPending
                    ? `Pending since ${dateLabel} — view pending users in User Management`
                    : `Inactive since ${dateLabel} — view deactivated users in User Management`;

                  return (
                    <div
                      key={`${variant}-${usr.id}`}
                      className={`user-mgmt-attention-card user-mgmt-attention-card--${variant}${canOpenUsers ? ' clickable' : ''}`}
                      title={canOpenUsers ? tooltip : dateLabel}
                      onClick={canOpenUsers ? () => openUsers(statusGroup) : undefined}
                      onKeyDown={canOpenUsers ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openUsers(statusGroup);
                        }
                      } : undefined}
                      role={canOpenUsers ? 'button' : undefined}
                      tabIndex={canOpenUsers ? 0 : undefined}
                    >
                      <div className="user-mgmt-attention-card__name">{usr.name}</div>
                      <div className="user-mgmt-attention-card__badges">
                        {usr.role && (
                          <span className="user-mgmt-attention-card__badge user-mgmt-attention-card__badge--role">
                            {formatRoleName(usr.role)}
                          </span>
                        )}
                        {usr.department && (
                          <span className="user-mgmt-attention-card__badge user-mgmt-attention-card__badge--dept">
                            {usr.department}
                          </span>
                        )}
                      </div>
                      <div className={`user-mgmt-attention-card__duration ${durationClass}`.trim()}>
                        {isPending ? `${days}d pending` : `${days}d inactive`}
                      </div>
                    </div>
                  );
                };

                const failedLoginTotal = securityMetrics?.failed_logins?.total || 0;
                const successRate = securityMetrics?.success_metrics?.success_rate || 0;
                const knownUsersWithFailures = (
                  securityMetrics?.failed_logins?.known_users_with_failures
                  ?? securityMetrics?.failed_logins?.unique_users
                  ?? 0
                );
                const identityProbeStats = securityMetrics?.failed_logins?.identity_probes || {
                  total_attempts: 0,
                  unique_usernames: 0,
                };
                const topUsers = getSortedLoginHealthRows(
                  securityMetrics?.failed_logins?.top_users?.slice(0, 10) || []
                );
                const showRiskColumn = topUsers.some(
                  (item) => item.risk_level && item.risk_level !== 'low'
                );

                return (
                  <>
                    <div className="user-mgmt-roster-toolbar">
                      <span className="user-mgmt-roster-toolbar__label">Roster &amp; roles</span>
                      {user_management.departments && user_management.departments.length > 0 && (
                        <select
                          className="user-mgmt-dept-select"
                          value={selectedDept}
                          onChange={(e) => handleDeptChange(e.target.value)}
                          disabled={loadingFilter}
                          aria-label="Filter roster and roles by department"
                        >
                          <option value="all">All departments</option>
                          {user_management.departments.map((dept) => (
                            <option key={dept.code} value={dept.code}>
                              {dept.icon} {dept.code}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>

                    {loadingFilter ? (
                      <div className="user-mgmt-loading">Loading roster…</div>
                    ) : (
                      <>
                        <div className="dashboard-stats-summary dashboard-stats-summary--with-readiness" style={{ marginBottom: '1rem' }}>
                          <StatsCard
                            icon="✅"
                            number={rosterActive}
                            label="Active"
                            className="active"
                            onClick={canOpenUsers ? () => openUsers('active') : null}
                            title={canOpenUsers ? 'View active users' : undefined}
                          />
                          <StatsCard
                            icon="⏸️"
                            number={rosterInactive}
                            label="Inactive"
                            className="deactivated"
                            onClick={canOpenUsers ? () => openUsers('deactivated') : null}
                            title={canOpenUsers ? 'View deactivated users' : undefined}
                          />
                          <StatsCard
                            icon="📧"
                            number={rosterPending}
                            label="Pending Verification"
                            className="pending"
                            onClick={canOpenUsers ? () => openUsers('pending') : null}
                            title={canOpenUsers ? 'View users pending verification' : undefined}
                          />
                          <div className="dashboard-stats-summary-separator" aria-hidden="true" />
                          <StatsCard
                            icon="🔑"
                            number={passwordSetupPending}
                            label="Password Setup Pending"
                            className="password-setup"
                            onClick={canOpenUsers ? () => openUsers('password_setup') : null}
                            title={
                              canOpenUsers
                                ? 'View active users who must set their password'
                                : 'Verified active users who must set their password before full app access'
                            }
                          />
                        </div>

                        <div className="dashboard-subsection-label">User distribution by role</div>
                        <div className="user-mgmt-role-table-wrap">
                          {roleList.length > 0 ? (
                            <table className="user-mgmt-role-table">
                              <thead>
                                <tr>
                                  <th className="user-mgmt-role-table__role-col">Role</th>
                                  <th className="user-mgmt-role-table__num-col user-mgmt-role-table__num-col--active">Active</th>
                                  <th className="user-mgmt-role-table__num-col user-mgmt-role-table__num-col--inactive">Inactive</th>
                                  <th className="user-mgmt-role-table__num-col user-mgmt-role-table__num-col--pending">Pending</th>
                                  <th className="user-mgmt-role-table__num-col">Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {roleList.map((roleItem) => {
                                  const activeCount = roleItem.count || 0;
                                  const inactiveCount = roleItem.inactive_count || 0;
                                  const pendingCount = roleItem.pending_count || 0;
                                  const total = activeCount + inactiveCount + pendingCount;
                                  return (
                                    <tr key={roleItem.role}>
                                      <td className="user-mgmt-role-table__role">{formatRoleName(roleItem.role)}</td>
                                      <td className="user-mgmt-role-table__num">{activeCount}</td>
                                      <td className="user-mgmt-role-table__num">{inactiveCount}</td>
                                      <td className="user-mgmt-role-table__num">{pendingCount}</td>
                                      <td className="user-mgmt-role-table__num user-mgmt-role-table__num--total">{total}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                              <tfoot>
                                <tr>
                                  <td className="user-mgmt-role-table__role user-mgmt-role-table__role--total">Total</td>
                                  <td className="user-mgmt-role-table__num user-mgmt-role-table__num--total">{rosterActive}</td>
                                  <td className="user-mgmt-role-table__num user-mgmt-role-table__num--total">{rosterInactive}</td>
                                  <td className="user-mgmt-role-table__num user-mgmt-role-table__num--total">{rosterPending}</td>
                                  <td className="user-mgmt-role-table__num user-mgmt-role-table__num--total">
                                    {rosterActive + rosterInactive + rosterPending}
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                          ) : (
                            <div className="user-mgmt-loading">No role data</div>
                          )}
                        </div>
                      </>
                    )}

                    {(inactiveKpi > 0 || pendingKpi > 0) && (
                      <div className="user-mgmt-needs-attention">
                        <h3 className="user-mgmt-needs-attention__title">Needs attention</h3>
                        <div
                          className={`user-mgmt-attention-columns${
                            inactiveKpi > 0 && pendingKpi > 0
                              ? ' user-mgmt-attention-columns--divided'
                              : ''
                          }`}
                        >
                          {inactiveKpi > 0 && (
                            <div className="user-mgmt-attention-group">
                              <div className="user-mgmt-attention-group__heading">
                                Inactive ({inactiveKpi})
                              </div>
                              <div className="user-mgmt-attention-cards">
                                {inactivePreview.map((usr) => renderAttentionCard(usr, 'inactive'))}
                              </div>
                              {inactiveKpi > inactivePreview.length && canOpenUsers && (
                                <button
                                  type="button"
                                  className="user-mgmt-view-all-link user-mgmt-view-all-link--inactive"
                                  onClick={() => openUsers('deactivated')}
                                >
                                  View all {inactiveKpi} in Users
                                </button>
                              )}
                            </div>
                          )}
                          {inactiveKpi > 0 && pendingKpi > 0 && (
                            <div
                              className="user-mgmt-attention-divider"
                              role="separator"
                              aria-orientation="vertical"
                              aria-hidden="true"
                            />
                          )}
                          {pendingKpi > 0 && (
                            <div className="user-mgmt-attention-group">
                              <div className="user-mgmt-attention-group__heading">
                                Pending verification ({pendingKpi})
                              </div>
                              <div className="user-mgmt-attention-cards">
                                {pendingPreview.map((usr) => renderAttentionCard(usr, 'pending'))}
                              </div>
                              {pendingKpi > pendingPreview.length && canOpenUsers && (
                                <button
                                  type="button"
                                  className="user-mgmt-view-all-link user-mgmt-view-all-link--pending"
                                  onClick={() => openUsers('pending')}
                                >
                                  View all {pendingKpi} in Users
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="user-mgmt-login-health">
                      <div className="user-mgmt-login-health__header">
                        <h3 className="user-mgmt-login-health__title">
                          Login health
                          <span className="user-mgmt-login-health__subtitle">Last 7 days</span>
                        </h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          {securityMetricsLoading && (
                            <span className="user-mgmt-loading-badge">Loading</span>
                          )}
                          <button
                            type="button"
                            className="user-mgmt-help-btn"
                            onClick={() => setShowLoginHealthHelp(true)}
                            aria-label="Login health help"
                            title="What do these metrics mean?"
                          >
                            ?
                          </button>
                        </div>
                      </div>

                      {securityMetrics && Object.keys(securityMetrics).length > 0 ? (
                        <>
                          <div className="dashboard-stats-summary">
                            <StatsCard
                              icon="🔐"
                              number={failedLoginTotal}
                              label="Failed Attempts"
                              className={failedLoginTotal > 5 ? 'open alert-elevated' : 'total'}
                              title="Total login failures in the last 7 days"
                            />
                            <StatsCard
                              icon="👤"
                              number={knownUsersWithFailures}
                              label="Known Users Affected"
                              className="total"
                              title="Distinct recognized users with at least one failed login"
                            />
                            {identityProbeStats.unique_usernames > 0 && (
                              <StatsCard
                                icon="🫥"
                                number={identityProbeStats.unique_usernames}
                                label="Unknown Usernames"
                                className="open alert-elevated"
                                title="Distinct usernames tried that do not match any account"
                              />
                            )}
                            <StatsCard
                              icon="✅"
                              number={formatPercentage(successRate)}
                              label="Success Rate"
                              className={successRate >= 90 ? 'active' : 'deactivated'}
                              title="Successful logins as a share of all attempts"
                            />
                          </div>

                          {securityMetrics.failed_logins?.by_reason
                            && Object.keys(securityMetrics.failed_logins.by_reason).length > 0 && (
                            <div className="user-mgmt-reason-chips">
                              {Object.entries(securityMetrics.failed_logins.by_reason).map(([reason, count]) => {
                                const info = getLoginReasonInfo(reason);
                                return (
                                  <span key={reason} className="user-mgmt-reason-chip">
                                    <span>{info.icon}</span>
                                    <span className="user-mgmt-reason-chip__count">{count}</span>
                                    <span>{info.label}</span>
                                  </span>
                                );
                              })}
                            </div>
                          )}

                          {topUsers.length > 0 && (
                            <div className="user-mgmt-login-table-wrap">
                              <table className="user-mgmt-login-table">
                                <thead>
                                  <tr>
                                    <th>
                                      <button
                                        type="button"
                                        className={`user-mgmt-login-table__sort-btn${loginHealthSortColumn === 'employee_id' ? ' user-mgmt-login-table__sort-btn--active' : ''}`}
                                        onClick={() => handleLoginHealthSort('employee_id')}
                                      >
                                        Employee ID
                                        {renderLoginHealthSortIndicator('employee_id')}
                                      </button>
                                    </th>
                                    <th>
                                      <button
                                        type="button"
                                        className={`user-mgmt-login-table__sort-btn${loginHealthSortColumn === 'full_name' ? ' user-mgmt-login-table__sort-btn--active' : ''}`}
                                        onClick={() => handleLoginHealthSort('full_name')}
                                      >
                                        Name
                                        {renderLoginHealthSortIndicator('full_name')}
                                      </button>
                                    </th>
                                    <th>
                                      <button
                                        type="button"
                                        className={`user-mgmt-login-table__sort-btn${loginHealthSortColumn === 'failed_attempts' ? ' user-mgmt-login-table__sort-btn--active' : ''}`}
                                        onClick={() => handleLoginHealthSort('failed_attempts')}
                                      >
                                        Attempts
                                        {renderLoginHealthSortIndicator('failed_attempts')}
                                      </button>
                                    </th>
                                    <th>
                                      <button
                                        type="button"
                                        className={`user-mgmt-login-table__sort-btn${loginHealthSortColumn === 'primary_reason' ? ' user-mgmt-login-table__sort-btn--active' : ''}`}
                                        onClick={() => handleLoginHealthSort('primary_reason')}
                                      >
                                        Reason
                                        {renderLoginHealthSortIndicator('primary_reason')}
                                      </button>
                                    </th>
                                    <th>
                                      <button
                                        type="button"
                                        className={`user-mgmt-login-table__sort-btn${loginHealthSortColumn === 'last_attempt' ? ' user-mgmt-login-table__sort-btn--active' : ''}`}
                                        onClick={() => handleLoginHealthSort('last_attempt')}
                                      >
                                        Last
                                        {renderLoginHealthSortIndicator('last_attempt')}
                                      </button>
                                    </th>
                                    {showRiskColumn && (
                                      <th>
                                        <button
                                          type="button"
                                          className={`user-mgmt-login-table__sort-btn${loginHealthSortColumn === 'risk_level' ? ' user-mgmt-login-table__sort-btn--active' : ''}`}
                                          onClick={() => handleLoginHealthSort('risk_level')}
                                        >
                                          Risk
                                          {renderLoginHealthSortIndicator('risk_level')}
                                        </button>
                                      </th>
                                    )}
                                  </tr>
                                </thead>
                                <tbody>
                                  {topUsers.map((item, idx) => (
                                    <tr key={`${item.employee_id}-${item.username || ''}-${idx}`}>
                                      <td style={{
                                        fontWeight: 600,
                                        color: item.is_identity_probe ? '#d32f2f' : '#333',
                                      }}
                                      >
                                        {item.is_identity_probe ? `🫥 ${item.employee_id}` : item.employee_id}
                                      </td>
                                      <td style={{
                                        color: item.is_identity_probe ? '#d32f2f' : '#666',
                                      }}
                                      >
                                        {item.full_name}
                                      </td>
                                      <td>
                                        <span className={`user-mgmt-attempts-badge ${item.failed_attempts > 5 ? 'user-mgmt-attempts-badge--high' : 'user-mgmt-attempts-badge--normal'}`}>
                                          {item.failed_attempts}
                                        </span>
                                      </td>
                                      <td>{formatLoginReason(item.primary_reason)}</td>
                                      <td>{formatRelativeAttempt(item.last_attempt)}</td>
                                      {showRiskColumn && (
                                        <td>
                                          <span className={`user-mgmt-risk-badge user-mgmt-risk-badge--${item.risk_level || 'low'}`}>
                                            {item.risk_color || '🟢'} {item.risk_level}
                                          </span>
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}

                          {failedLoginTotal > 5 && (
                            <div className="user-mgmt-alert-banner">
                              High number of failed login attempts detected. Consider reviewing these accounts for suspicious activity.
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="user-mgmt-loading">No security metrics available</div>
                      )}
                    </div>

                    <LoginHealthHelpModal
                      isOpen={showLoginHealthHelp}
                      onClose={() => setShowLoginHealthHelp(false)}
                    />
                  </>
                );
              })()}
            </>
          ) : (
            <EmptyState
              section="User Management"
              message="All users are active and in good standing."
              icon="✅"
            />
          )}
        </Section>

        {/* ========================================
            SECTION 6: DEVICE AUDIT & COMPLIANCE TRACKING
            ======================================== */}
        <Section
          id="audit-compliance"
          title={(
            <span
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                gap: '1rem',
              }}
            >
              <span>🔍 Device Audit & Compliance Tracking</span>
              <button
                type="button"
                className="user-mgmt-help-btn"
                onClick={() => setShowDeviceAuditHelp(true)}
                aria-label="Device audit and compliance help"
                title="What do these metrics mean?"
                style={{ flexShrink: 0 }}
              >
                ?
              </button>
            </span>
          )}
        >
          {hasAuditComplianceData ? (
            <AuditComplianceSection
              audit_events={audit_events}
              compliance_status={compliance_status}
              auditDepartmentFilter={auditDepartmentFilter}
              setAuditDepartmentFilter={setAuditDepartmentFilter}
              auditQuickFilter={auditQuickFilter}
              setAuditQuickFilter={setAuditQuickFilter}
              selectedAuditEvent={selectedAuditEvent}
              setSelectedAuditEvent={setSelectedAuditEvent}
              isAuditDrawerOpen={isAuditDrawerOpen}
              setIsAuditDrawerOpen={setIsAuditDrawerOpen}
              deptSearchQuery={deptSearchQuery}
              setDeptSearchQuery={setDeptSearchQuery}
              nonCompliantSearchQuery={nonCompliantSearchQuery}
              setNonCompliantSearchQuery={setNonCompliantSearchQuery}
              showDeviceAuditHelp={showDeviceAuditHelp}
              setShowDeviceAuditHelp={setShowDeviceAuditHelp}
              onNavigateToDevices={onNavigateToDevices}
            />
          ) : (
            <EmptyState
              section="Audit & Compliance"
              message="No audit or compliance data available yet."
              icon="📋"
            />
          )}
        </Section>

        {/* ========================================
            SECTION 7: SECURITY TRACKING & MONITORING
            ======================================== */}
        <Section
          id="security-tracking"
          title={(
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                gap: '1rem',
              }}
            >
              <span>🔒 Security Tracking & Monitoring</span>
              <button
                type="button"
                className="user-mgmt-help-btn"
                onClick={() => setShowSecurityHelp(true)}
                aria-label="Security tracking help"
                title="What do these metrics mean?"
                style={{ flexShrink: 0 }}
              >
                ?
              </button>
            </span>
          )}
        >
          {hasSecurityTrackingData ? (
            <SecurityTrackingSection
              securityMetrics={securityMetrics}
              securityMetricsLoading={securityMetricsLoading}
              userManagement={user_management}
              showSecurityHelp={showSecurityHelp}
              setShowSecurityHelp={setShowSecurityHelp}
              onScrollToSection={scrollToSection}
            />
          ) : (
            <EmptyState
              section="Security Tracking"
              message="Security insights will appear once authentication and session data are available."
              icon="🔒"
            />
          )}
        </Section>

            {/* ========================================
                COMPLAINT VIEW MODAL
                Displays complaint details when a row is clicked
                ======================================== */}
            {selectedComplaintForModal && (
              <ComplaintView
                complaintId={selectedComplaintForModal.id}
                token={token}
                onClose={() => setSelectedComplaintForModal(null)}
                onUpdate={() => {
                  // For admin dashboard, refresh might not be necessary as they see aggregated data
                  // But we can add it for consistency if needed
                }}
                isFullPage={false}
                user={user}
              />
            )}

            {/* Expanded Usernames Modal */}
            {expandedUsernamesRow && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
              }}>
                <div style={{
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  padding: '2rem',
                  maxWidth: '500px',
                  width: '90%',
                  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem'
                }}>
                  {/* Header */}
                  <div>
                    <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem', color: '#333' }}>
                      All Attempted Usernames
                    </h2>
                    <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                      {expandedUsernamesRow.fullName} ({expandedUsernamesRow.employeeId})
                    </p>
                  </div>

                  {/* Usernames List */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    backgroundColor: '#f9fafb',
                    padding: '1rem',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb'
                  }}>
                    {expandedUsernamesRow.usernames.map((username, idx) => (
                      <div key={idx} style={{
                        padding: '0.75rem',
                        backgroundColor: '#fff',
                        borderRadius: '4px',
                        border: '1px solid #e5e7eb',
                        fontFamily: 'monospace',
                        fontSize: '0.9rem',
                        color: '#333',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <span>{username}</span>
                        <span style={{ color: '#999', fontSize: '0.8rem' }}>#{idx + 1}</span>
                      </div>
                    ))}
                  </div>

                  {/* Footer with count */}
                  <div style={{
                    padding: '0.75rem',
                    backgroundColor: '#f0fdf4',
                    borderRadius: '6px',
                    border: '1px solid #bbf7d0',
                    fontSize: '0.9rem',
                    color: '#15803d',
                    fontWeight: '500'
                  }}>
                    Total: {expandedUsernamesRow.usernames.length} usernames
                  </div>

                  {/* Close Button */}
                  <button
                    onClick={() => setExpandedUsernamesRow(null)}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: 'var(--dp-accent-primary)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--dp-accent-primary)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--dp-accent-primary)'}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
      </DashboardLayout>
        );
      }}
    </BaseDashboard>
  );
};

export default AdminDashboard;
